/**
 * Dashboard do Super Admin
 * 
 * Visão geral de todas as empresas e métricas do sistema
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi } from '../../services/superadmin/dashboardApiService';
import type { DashboardStats, TimelineDataPoint, TopTenant } from '../../types/superadmin/dashboard.types';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SuperAdminNav } from '../../components/SuperAdminNav';
import { useTheme } from '../../contexts/ThemeContext';

type PeriodType = '7days' | '30days' | '3months' | '6months' | 'lastyear' | 'custom';

export const SuperAdminDashboardPage = () => {
  console.log(`%c[PAI Layout] SuperAdminDashboardPage Renderizou`, 'color: #ffaa00; font-weight: bold;');
  const { theme } = useTheme();
  const navRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [timeline, setTimeline] = useState<TimelineDataPoint[]>([]);
  const [topTenants, setTopTenants] = useState<TopTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtro de período
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('30days');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case '7days': return 'Últimos 7 dias';
      case '30days': return 'Últimos 30 dias';
      case '3months': return 'Últimos 3 meses';
      case '6months': return 'Últimos 6 meses';
      case 'lastyear': return 'Ano passado';
      case 'custom': return customStartDate && customEndDate 
        ? `${customStartDate} ~ ${customEndDate}` 
        : 'Intervalo de datas personalizado';
      default: return 'Últimos 30 dias';
    }
  };

  const getPeriodDays = (): number => {
    switch (selectedPeriod) {
      case '7days': return 7;
      case '30days': return 30;
      case '3months': return 90;
      case '6months': return 180;
      case 'lastyear': return 365;
      case 'custom': {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          const diffTime = Math.abs(end.getTime() - start.getTime());
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
        return 30;
      }
      default: return 30;
    }
  };

  const handlePeriodChange = (period: PeriodType) => {
    setSelectedPeriod(period);
    setShowPeriodDropdown(false);
    
    if (period === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const days = getPeriodDays();

      // Carregar dados em paralelo
      const [statsData, timelineData, topTenantsData] = await Promise.all([
        dashboardApi.getDashboardStats(),
        dashboardApi.getDispatchesTimeline(days),
        dashboardApi.getTopTenants(10)
      ]);

      setStats(statsData);
      setTimeline(timelineData.timeline);
      setTopTenants(topTenantsData.topTenants);
    } catch (err: any) {
      console.error('Erro ao carregar dashboard:', err);
      setError(err.message || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // NÃO processar se o clique foi no SuperAdminNav
      if (navRef.current && navRef.current.contains(target)) {
        return;
      }
      
      if (!target.closest('.period-filter')) {
        setShowPeriodDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setShowDatePicker(false);
      loadData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-3xl font-bold">❌ Erro</p>
          <p className="mt-2 text-sm font-normal">{error || 'Erro ao carregar dados'}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  return (
    <>
      <SuperAdminNav ref={navRef} />
      <div className="container mx-auto p-6">
        {/* MODIFICADO: Header com Filtro de Período - cores adaptadas para tema dark */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 
                className="text-3xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                Dashboard Super Admin
              </h1>
              <p 
                className="mt-2 text-sm font-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                Visão geral de todas as empresas e métricas do sistema
              </p>
            </div>
            
            {/* Filtro de Período */}
            <div className="relative period-filter">
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-card)';
                }}
              >
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span 
                  className="text-sm font-normal"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {getPeriodLabel()}
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${showPeriodDropdown ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* MODIFICADO: Dropdown de Períodos - adaptado para tema dark */}
              {showPeriodDropdown && (
                <div 
                  className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-50"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div className="py-1">
                    {(['7days', '30days', '3months', '6months', 'lastyear'] as PeriodType[]).map((period) => (
                      <button
                        key={period}
                        onClick={() => handlePeriodChange(period)}
                        className="w-full text-left px-4 py-2 text-sm transition-colors"
                        style={{
                          backgroundColor: selectedPeriod === period 
                            ? (theme === 'dark' ? '#1e3a5f' : '#eff6ff')
                            : 'transparent',
                          color: selectedPeriod === period
                            ? (theme === 'dark' ? '#bfdbfe' : '#1e40af')
                            : 'var(--text-primary)'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPeriod !== period) {
                            e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPeriod !== period) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {period === '7days' && 'Últimos 7 dias'}
                        {period === '30days' && 'Últimos 30 dias'}
                        {period === '3months' && 'Últimos 3 meses'}
                        {period === '6months' && 'Últimos 6 meses'}
                        {period === 'lastyear' && 'Ano passado'}
                      </button>
                    ))}
                    <div 
                      className="border-t mt-1 pt-1"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <button
                        onClick={() => handlePeriodChange('custom')}
                        className="w-full text-left px-4 py-2 text-sm transition-colors"
                        style={{
                          backgroundColor: selectedPeriod === 'custom' 
                            ? (theme === 'dark' ? '#1e3a5f' : '#eff6ff')
                            : 'transparent',
                          color: selectedPeriod === 'custom'
                            ? (theme === 'dark' ? '#bfdbfe' : '#1e40af')
                            : 'var(--text-primary)'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPeriod !== 'custom') {
                            e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPeriod !== 'custom') {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        Intervalo de datas personalizado
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* MODIFICADO: Date Picker Personalizado - adaptado para tema dark */}
              {showDatePicker && selectedPeriod === 'custom' && (
                <div 
                  className="absolute right-0 mt-2 rounded-lg shadow-lg z-50 p-4 w-80"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <h3 
                    className="text-sm font-semibold mb-3"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Selecionar Período
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label 
                        className="block text-xs font-medium mb-1"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Data inicial
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)'
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-xs font-medium mb-1"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Data final
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)'
                        }}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => {
                          setShowDatePicker(false);
                          setSelectedPeriod('30days');
                        }}
                        className="px-3 py-1.5 text-sm rounded transition-colors"
                        style={{
                          color: 'var(--text-primary)',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={applyCustomDateRange}
                        disabled={!customStartDate || !customEndDate}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* MODIFICADO: Cards de Métricas - adaptados para tema dark */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total de Empresas */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                Total de Empresas
              </p>
              <p 
                className="text-3xl font-bold mt-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {stats.tenants.total}
              </p>
              <p className="text-sm font-normal text-green-600 mt-1">
                +{stats.tenants.newThisMonth} este mês ({stats.tenants.growthRate.toFixed(1)}%)
              </p>
            </div>
            <div className="text-4xl">🏢</div>
          </div>
        </div>

        {/* Números WhatsApp */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                Números WhatsApp
              </p>
              <p 
                className="text-3xl font-bold mt-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {stats.whatsappWebSessions.connected}
              </p>
              <p className="text-sm font-normal text-blue-600 mt-1">
                {stats.whatsappWebSessions.availabilityRate.toFixed(1)}% online
              </p>
            </div>
            <div className="text-4xl">📱</div>
          </div>
        </div>

        {/* Disparos Hoje */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                Disparos Hoje
              </p>
              <p 
                className="text-3xl font-bold mt-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {stats.dispatches.combined.today.toLocaleString()}
              </p>
              <p className="text-sm font-normal text-purple-600 mt-1">
                {stats.dispatches.combined.thisMonth.toLocaleString()} este mês
              </p>
            </div>
            <div className="text-4xl">📤</div>
          </div>
        </div>

        {/* Campanhas Ativas */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-sm font-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                Campanhas Ativas
              </p>
              <p 
                className="text-3xl font-bold mt-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {stats.campaigns.running}
              </p>
              <p className="text-sm font-normal text-orange-600 mt-1">
                {stats.campaigns.scheduled} agendadas
              </p>
            </div>
            <div className="text-4xl">🚀</div>
          </div>
        </div>
      </div>

      {/* MODIFICADO: Gráficos - adaptados para tema dark */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Timeline de Disparos */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Disparos ({getPeriodLabel()})
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={theme === 'dark' ? '#3f3f46' : '#e5e7eb'} 
              />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}/${date.getMonth() + 1}`;
                }}
                stroke={theme === 'dark' ? '#a1a1aa' : '#6b7280'}
                tick={{ fill: theme === 'dark' ? '#a1a1aa' : '#6b7280' }}
              />
              <YAxis 
                stroke={theme === 'dark' ? '#a1a1aa' : '#6b7280'}
                tick={{ fill: theme === 'dark' ? '#a1a1aa' : '#6b7280' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme === 'dark' ? '#1e1e1f' : '#ffffff',
                  border: `1px solid ${theme === 'dark' ? '#3f3f46' : '#e5e7eb'}`,
                  color: theme === 'dark' ? '#e4e4e7' : '#1e293b'
                }}
              />
              <Legend 
                wrapperStyle={{ color: theme === 'dark' ? '#e4e4e7' : '#1e293b' }}
              />
              <Line type="monotone" dataKey="whatsappWeb" stroke="#3B82F6" name="WhatsApp Web" />
              <Line type="monotone" dataKey="officialApi" stroke="#10B981" name="API Oficial" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Distribuição de Disparos */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Distribuição de Disparos
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'WhatsApp Web', value: stats.dispatches.whatsappWeb.thisMonth },
                  { name: 'API Oficial', value: stats.dispatches.officialApi.thisMonth }
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {[0, 1].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme === 'dark' ? '#1e1e1f' : '#ffffff',
                  border: `1px solid ${theme === 'dark' ? '#3f3f46' : '#e5e7eb'}`,
                  color: theme === 'dark' ? '#e4e4e7' : '#1e293b'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* MODIFICADO: Estatísticas Detalhadas - adaptadas para tema dark */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* WhatsApp Web Stats */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            📱 WhatsApp Web
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Total:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.dispatches.whatsappWeb.total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Este mês:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.dispatches.whatsappWeb.thisMonth.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Taxa de sucesso:</span>
              <span className="font-semibold text-green-600">
                {stats.dispatches.whatsappWeb.successRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* API Oficial Stats */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            🎯 API Oficial
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Total:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.dispatches.officialApi.total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Este mês:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.dispatches.officialApi.thisMonth.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Taxa de entrega:</span>
              <span className="font-semibold text-blue-600">
                {stats.dispatches.officialApi.deliveryRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Taxa de leitura:</span>
              <span className="font-semibold text-purple-600">
                {stats.dispatches.officialApi.readRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* Limites */}
        <div 
          className="rounded-lg shadow p-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            🚦 Limites de Disparos
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Disponível:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.limits.totalAvailable.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-normal" style={{ color: 'var(--text-secondary)' }}>Usado:</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stats.limits.totalUsed.toLocaleString()}
              </span>
            </div>
            <div className="mt-2">
              <div className="flex justify-between text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-normal">Uso geral:</span>
                <span className="font-normal">{stats.limits.usagePercentage.toFixed(1)}%</span>
              </div>
              <div 
                className="w-full rounded-full h-2"
                style={{ backgroundColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb' }}
              >
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(stats.limits.usagePercentage, 100)}%` }}
                ></div>
              </div>
            </div>
            {stats.limits.tenantsNearLimit > 0 && (
              <div 
                className="mt-3 p-2 rounded"
                style={{
                  backgroundColor: theme === 'dark' ? '#422006' : '#fef3c7',
                  border: `1px solid ${theme === 'dark' ? '#713f12' : '#fcd34d'}`
                }}
              >
                <p 
                  className="text-sm font-normal"
                  style={{ color: theme === 'dark' ? '#fbbf24' : '#92400e' }}
                >
                  ⚠️ {stats.limits.tenantsNearLimit} empresa(s) próxima(s) do limite
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODIFICADO: Top Empresas - tabela adaptada para tema dark */}
      <div 
        className="rounded-lg shadow p-6"
        style={{
          backgroundColor: 'var(--bg-card)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-color)'
        }}
      >
        <h2 
          className="text-xl font-semibold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          🏆 Top 10 Empresas Mais Ativas
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
            <thead>
              <tr>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  #
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Empresa
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Disparos (Mês)
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Campanhas Ativas
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Taxa de Sucesso
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {topTenants.map((tenant, index) => (
                <tr 
                  key={tenant.tenantId} 
                  className="transition-colors"
                  style={{
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm font-normal"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {index + 1}
                  </td>
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm font-normal"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {tenant.tenantName}
                  </td>
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm font-normal"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {tenant.dispatchesThisMonth.toLocaleString()}
                  </td>
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm font-normal"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {tenant.campaignsActive}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                    {tenant.successRate.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </>
  );
};

export default SuperAdminDashboardPage;

